import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FaMicrophone,
  FaPaperPlane,
  FaStop,
  FaArrowLeft,
} from "react-icons/fa";
import axios from "axios";
import "./Ivawithhub.css";

const hubs = [
  "Ignite-FAQS",
  "Core",
  "GenAI and Agentic AI Hub",
  "Sales and Solutioning",
  "Delivery Modernization",
  "Product",
];

const Iva = ({ messages, setMessages }) => {
  const [input, setInput] = useState("");
  const [hub, setHub] = useState("");
  const [isApiInProgress, setIsApiInProgress] =
    useState(false);
  const [isRecording, setIsRecording] =
    useState(false);

  // Detect compact popup
  const [isCompactView, setIsCompactView] =
    useState(false);

  const inputRef = useRef(null);
  const recognition = useRef(null);
  const speechEndTimeout = useRef(null);
  const chatEndRef = useRef(null);
  const containerRef = useRef(null);

  /* =========================
     AUTO SCROLL
  ========================= */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, isApiInProgress]);

  /* =========================
     COMPACT VIEW DETECTION
  ========================= */
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;

        // Compact popup threshold
        setIsCompactView(width < 500);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /* =========================
     HUB SELECT
  ========================= */
  const handleHubSelect = (value) => {
    const selectedHub =
      value === "Ignite-FAQS"
        ? "ignite-general"
        : value;

    setHub(selectedHub);
  };

  /* =========================
     HUB RESET
  ========================= */
  const handleHubReset = () => {
    setHub("");
  };

  /* =========================
     MIC HANDLER
  ========================= */
  const handleMicClick = () => {
    if (isRecording) {
      recognition.current?.stop();
      return;
    }

    recognition.current =
      new (window.SpeechRecognition ||
        window.webkitSpeechRecognition)();

    recognition.current.lang = "en-US";
    recognition.current.continuous = false;
    recognition.current.interimResults = false;

    recognition.current.onstart = () => {
      setIsRecording(true);
    };

    recognition.current.onspeechend = () => {
      speechEndTimeout.current = setTimeout(() => {
        recognition.current?.stop();
      }, 3000);
    };

    recognition.current.onresult = (event) => {
      clearTimeout(speechEndTimeout.current);

      setInput(event.results[0][0].transcript);

      setIsRecording(false);

      inputRef.current?.focus();
    };

    recognition.current.onerror = () => {
      setIsRecording(false);
    };

    recognition.current.onend = () => {
      setIsRecording(false);
    };

    recognition.current.start();
  };

  /* =========================
     SEND HANDLER
  ========================= */
  const handleSend = () => {
    if (!input.trim()) return;

    if (!hub) {
      alert("Please select a hub first");
      return;
    }

    console.log({
      hub,
      message: input,
      user: localStorage.user,
      time: new Date().toISOString(),
    });

    const messageText = input;

    setInput("");

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        text: messageText,
        isUser: true,
      },
    ]);

    setIsApiInProgress(true);

    axios({
      url: "https://wa278175geninaeuwa02-apim.azure-api.net/getAccentureContent",
      method: "post",
      data: {
        skill: hub,
        prompt: messageText,
        user: localStorage.user?.split("@")[0],
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `${localStorage.token}`,
      },
    })
      .then((res) => {
        setMessages((prev) => [
          ...prev,
          {
            text:
              res.data?.[0] ||
              "No response was received. Please try again.",
            isUser: false,
          },
        ]);
      })
      .catch((err) => {
        console.error(err);

        setMessages((prev) => [
          ...prev,
          {
            text:
              "I had trouble understanding that. Could you try rephrasing your message?",
            isUser: false,
          },
        ]);
      })
      .finally(() => {
        setIsApiInProgress(false);
      });
  };

  /* =========================
     USER INITIALS
  ========================= */
  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((p) => p.trim()[0])
      .join("")
      .toUpperCase();

  return (
    <div
      className="ignite-container popup"
      ref={containerRef}
    >
      <div className="ignite-chat-wrapper">

        {/* SELECTED HUB BAR */}
        {hub && (
          <div className="selected-hub-bar">
            <button
              className="hub-back-btn"
              onClick={handleHubReset}
            >
              <FaArrowLeft />
            </button>

            <div className="selected-hub-text">
              {hub}
            </div>
          </div>
        )}

        {/* CHAT */}
        <div className="ignite-chat">

          {/* HUB SELECTION */}
          {!hub && (
            <div className="ignite-message bot">
              <div className="ignite-avatar">
                <img
                  src="./Assets/askivabot.png"
                  alt="Bot"
                />
              </div>

              <div className="ignite-bubble bot">
                <p>Please select a hub to start:</p>

                {isCompactView ? (
                  /* DROPDOWN */
                  <select
                    className="hub-dropdown"
                    value=""
                    onChange={(e) =>
                      handleHubSelect(e.target.value)
                    }
                  >
                    <option value="">
                      Select Hub
                    </option>

                    {hubs.map((h) => (
                      <option
                        key={h}
                        value={h}
                      >
                        {h}
                      </option>
                    ))}
                  </select>
                ) : (
                  /* BUTTONS */
                  <div className="category-box-container">
                    {hubs.map((h) => (
                      <div
                        key={h}
                        className="category-box"
                        onClick={() =>
                          handleHubSelect(h)
                        }
                      >
                        {h}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MESSAGES */}
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`ignite-message ${
                msg.isUser ? "user" : "bot"
              }`}
            >
              {!msg.isUser && (
                <div className="ignite-avatar">
                  <img
                    src="./Assets/askivabot.png"
                    alt="Bot"
                  />
                </div>
              )}

              <div
                className={`ignite-bubble ${
                  msg.isUser ? "user" : "bot"
                }`}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                >
                  {msg.text}
                </ReactMarkdown>
              </div>

              {msg.isUser && (
                <div className="ignite-avatar user">
                  {getInitials(localStorage.name)}
                </div>
              )}
            </div>
          ))}

          {/* TYPING */}
          {isApiInProgress && (
            <div className="ignite-message bot">
              <div className="ignite-avatar">
                <img
                  src="./Assets/askivabot.png"
                  alt="Bot"
                />
              </div>

              <div className="ignite-bubble bot">
                <span className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* INPUT */}
        <div className="ignite-input-section">
          <div
            className={`ignite-input-box ${
              input ? "active" : ""
            }`}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) =>
                setInput(e.target.value)
              }
              onKeyDown={(e) =>
                e.key === "Enter" && handleSend()
              }
              placeholder={
                hub
                  ? "Ask me anything..."
                  : "Select a hub first..."
              }
              disabled={!hub}
            />

            <div className="ignite-input-buttons">
              {!isRecording ? (
                <FaMicrophone
                  className="ignite-input-icon mic"
                  onClick={handleMicClick}
                />
              ) : (
                <FaStop
                  className="ignite-input-icon mic recording"
                  onClick={() =>
                    recognition.current?.stop()
                  }
                />
              )}

              <FaPaperPlane
                className={`ignite-input-icon send ${
                  !hub ? "disabled" : ""
                }`}
                onClick={handleSend}
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Iva;